<script setup lang="ts">
  import { useTemplateRef } from 'vue'
  import { ListboxFilter } from 'reka-ui'
  import { Badge, Button } from '@gat-ship/vue-components'
  import { IconClose, IconLoading, IconSearch } from '@gat-ship/icons'
  import { testIds } from '@gat-ship/types'
  import { useGlobalSearch } from '../composables/global-search-context'
  import { useGlobalSearchContextMetadata } from '../composables/useGlobalSearchContextMetadata'

  const {
    searchInputValue,
    selectedSearchContext,
    isTagActive,
    isSearching,
    isSearchInputEmpty,
    setInput,
    clearTag,
    clearAll
  } = useGlobalSearch()

  const contextMetadata = useGlobalSearchContextMetadata()

  const filterRef = useTemplateRef('filterRef')

  function focusInput() {
    const element = filterRef.value?.$el as HTMLInputElement | undefined
    element?.focus()
  }

  function onBackspace(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement | null
    const caretAtStart = input?.selectionStart === 0
    if (isSearchInputEmpty.value || caretAtStart) {
      clearTag()
    }
  }

  function onClear() {
    clearAll()
    focusInput()
  }

  defineExpose({ focusInput })
</script>

<template>
  <div class="flex h-12 items-center gap-2 border-b px-3">
    <icon-search class="size-4 shrink-0 opacity-50" />
    <Badge
      v-if="isTagActive"
      variant="outline"
      class="shrink-0"
      :data-testid="testIds.globalSearch.tagBadge"
    >
      {{ contextMetadata[selectedSearchContext].prefix }}:
    </Badge>
    <ListboxFilter
      ref="filterRef"
      :model-value="searchInputValue"
      auto-focus
      placeholder="Search..."
      class="placeholder:text-muted-foreground outline-hidden flex h-12 w-full bg-transparent py-3 text-sm"
      :data-testid="testIds.globalSearch.input"
      @update:model-value="setInput($event ?? '')"
      @keydown.backspace="onBackspace"
    />
    <IconLoading
      v-if="isSearching"
      class="text-muted-foreground size-4 shrink-0 animate-spin"
    />
    <Button
      v-if="searchInputValue || isTagActive"
      class="text-muted-foreground rounded-full"
      variant="ghost"
      size="icon"
      aria-label="Clear search"
      :data-testid="testIds.globalSearch.clearButton"
      @click="onClear"
    >
      <IconClose class="size-4" />
    </Button>
  </div>
</template>
